"use client";

import { memo, useCallback, useMemo } from "react";
import { FileTextIcon, FolderOpenIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGeneratedDocumentStore } from "@/components/document/generatedDocumentStore";
import { usePraetorRevisionStore } from "@/components/custom/stores/praetorRevisionStore";
import { openGeneratedDocumentInSidebar, openDocumentInSidebar } from "@/lib/threadDocumentOps";
import { getLiveThreadId } from "@/components/assistant";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Composer pill that surfaces all thread documents (generated + Praetor revisions)
 * in a lightweight popover. Clicking an item opens the full PDF sidebar.
 * Only renders when the thread has at least one document.
 */
export const ComposerDocumentsPill = memo(function ComposerDocumentsPill() {
  const { t } = useTranslation();

  const generatedEntries = useGeneratedDocumentStore((s) => s.entries);
  const praetorRevisions = usePraetorRevisionStore((s) => s.entries);

  const generatedList = useMemo(
    () => Object.values(generatedEntries).sort((a, b) => a.order - b.order),
    [generatedEntries],
  );
  const revisionList = useMemo(
    () => Object.values(praetorRevisions),
    [praetorRevisions],
  );

  const totalCount = generatedList.length + revisionList.length;
  if (totalCount === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 rounded-full border border-border/60 bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0 cursor-pointer"
          title={t("composerDocumentsPill.tooltip")}
        >
          <FolderOpenIcon className="size-3.5" />
          <span>{totalCount}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="top"
        className="w-64 max-h-72 overflow-y-auto p-1.5"
      >
        {generatedList.length > 0 && (
          <DocSection label={t("composerDocumentsPill.generated")}>
            {generatedList.map((entry) => (
              <DocItem
                key={entry.documentId}
                title={entry.title ?? entry.suggestedFileName ?? `document-${entry.documentId.slice(0, 8)}`}
                variant="generated"
                onClick={() => openGeneratedDocumentInSidebar(entry.documentId, entry.title, getLiveThreadId())}
              />
            ))}
          </DocSection>
        )}
        {revisionList.length > 0 && (
          <DocSection label={t("composerDocumentsPill.revisions")}>
            {revisionList.map((entry) => (
              <DocItem
                key={entry.praetorDocumentId}
                title={entry.title ?? entry.praetorDocumentId.slice(0, 8)}
                variant="revision"
                onClick={() => openDocumentInSidebar(entry.praetorDocumentId, undefined, getLiveThreadId())}
              />
            ))}
          </DocSection>
        )}
      </PopoverContent>
    </Popover>
  );
});

function DocSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-1 last:mb-0">
      <div className="px-2 py-1 text-[10px] font-semibold uppercase text-muted-foreground/70 tracking-wider">
        {label}
      </div>
      {children}
    </div>
  );
}

function DocItem({
  title,
  variant,
  onClick,
}: {
  title: string;
  variant: "generated" | "revision";
  onClick: () => void;
}) {
  const handleClick = useCallback(() => {
    onClick();
  }, [onClick]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-muted/60 transition-colors cursor-pointer"
      title={title}
    >
      <FileTextIcon
        className="size-3.5 shrink-0 text-primary"
      />
      <span className="truncate flex-1">{title}</span>
    </button>
  );
}
